import { useCallback } from 'react'
// The compat router returns null under the App Router instead of throwing;
// headers and footers render under both routers.
import { useRouter } from 'next/compat/router'
import { AnalyticsEventName } from '@dupe/common/constants'
import useAnalytics from '~/src/hooks/useAnalytics'

export type NavLocation = 'header' | 'desktop-nav' | 'menu' | 'footer'

// Nav areas nest (the desktop nav sits inside the header), so one click
// bubbles through several tracked containers. The innermost one records it;
// the rest see the event here and skip.
const trackedClicks = new WeakSet<Event>()

// Under the App Router there is no Pages Router pattern to read, so map the
// URL to a fixed, id-free pattern. Unknown paths (including not-found pages,
// which can be any typed URL) collapse to one bucket.
const APP_ROUTER_SECTIONS = ['reports', 'admin']

export function appRouterRoutePattern(pathname: string): string {
  const [section, ...rest] = pathname.split('/').filter(Boolean)
  if (!section) return '/'
  if (!APP_ROUTER_SECTIONS.includes(section)) return '(app-router)'
  return rest.length > 0 ? `/${section}/[...]` : `/${section}`
}

/**
 * Returns an `onClick` for a nav container that sends one `Nav Link Clicked`
 * event per link click inside it. One listener per area instead of one per
 * link keeps every current and future link covered.
 */
export function useNavClickTracker(location: NavLocation) {
  const analytics = useAnalytics()
  const router = useRouter()

  return useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const native = event.nativeEvent
      if (trackedClicks.has(native)) return

      const target = event.target as Element | null
      const anchor = target?.closest?.('a[href]')
      if (!anchor || !event.currentTarget.contains(anchor)) return

      trackedClicks.add(native)
      analytics.track(
        AnalyticsEventName.NavLinkClicked,
        {
          location,
          href: anchor.getAttribute('href'),
          label:
            (
              anchor.textContent?.trim() || anchor.getAttribute('aria-label')
            )?.slice(0, 60) || null,
          from_route:
            router?.pathname ?? appRouterRoutePattern(window.location.pathname),
        },
        { skipGA: true }
      )
    },
    [analytics, location, router?.pathname]
  )
}
